import type React from "react";
import { Link, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Linkedin, Github } from "lucide-react";
import { useState } from "react";

const navLinkStyle: React.CSSProperties = {
  color: "var(--dp-muted)",
  textDecoration: "none",
  fontSize: "0.82rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  transition: "color 0.2s",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
  padding: 0,
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isHomePage = location.pathname === "/";

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const resourceItems = [
    { title: "Templates & Frameworks", href: "/resources/templates-and-frameworks" },
    { title: "AI Tools", href: "/resources/ai-tools" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "var(--navy)" }}>
      {/* ── Nav ──────────────────────────────────────────── */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1.4rem 5vw",
          background: "rgba(15,21,32,0.85)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid var(--border-gold)",
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: "1.35rem",
            fontWeight: 400,
            letterSpacing: "0.04em",
            color: "var(--dp-white)",
            textDecoration: "none",
          }}
        >
          Roberto<span style={{ color: "var(--gold)" }}> Pesce</span>
        </Link>

        {/* Desktop nav links */}
        <ul
          style={{ display: "flex", gap: "2.5rem", listStyle: "none", margin: 0, padding: 0 }}
          className="nav-desktop"
        >
          {isHomePage ? (
            <>
              <li>
                <button style={navLinkStyle} onClick={() => scrollTo("stories")}>
                  Work
                </button>
              </li>
              <li>
                <button style={navLinkStyle} onClick={() => scrollTo("about")}>
                  About
                </button>
              </li>
              <li>
                <button style={navLinkStyle} onClick={() => scrollTo("building")}>
                  Building
                </button>
              </li>
              <li>
                <button style={navLinkStyle} onClick={() => scrollTo("contact")}>
                  Contact
                </button>
              </li>
              <li>
                <a
                  href="https://ehazzhswtowuskdmgibz.supabase.co/storage/v1/object/public/pescelab-main-site/CV%20Roberto%20Pesce.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    ...navLinkStyle,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.45rem",
                    border: "1px solid var(--border-gold)",
                    color: "var(--gold)",
                    padding: "0.5rem 1.2rem",
                    borderRadius: "2px",
                    fontSize: "0.76rem",
                    letterSpacing: "0.12em",
                    transition: "background 0.2s, border-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "rgba(201,169,110,0.1)";
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--gold)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "transparent";
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--border-gold)";
                  }}
                >
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 13, height: 13 }}>
                    <path d="M8 2v8M5 7l3 3 3-3M3 12h10" />
                  </svg>
                  CV
                </a>
              </li>
            </>
          ) : (
            <>
              <li>
                <Link to="/" style={navLinkStyle}>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/resources" style={navLinkStyle}>
                  Resources
                </Link>
              </li>
              <li>
                <a
                  href="https://ehazzhswtowuskdmgibz.supabase.co/storage/v1/object/public/pescelab-main-site/CV%20Roberto%20Pesce.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    ...navLinkStyle,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.45rem",
                    border: "1px solid var(--border-gold)",
                    color: "var(--gold)",
                    padding: "0.5rem 1.2rem",
                    borderRadius: "2px",
                    fontSize: "0.76rem",
                    letterSpacing: "0.12em",
                    transition: "background 0.2s, border-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "rgba(201,169,110,0.1)";
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--gold)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "transparent";
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--border-gold)";
                  }}
                >
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 13, height: 13 }}>
                    <path d="M8 2v8M5 7l3 3 3-3M3 12h10" />
                  </svg>
                  CV
                </a>
              </li>
            </>
          )}
        </ul>

        {/* Mobile menu */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <button
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--dp-white)",
                display: "none",
                minHeight: 44,
                minWidth: 44,
                alignItems: "center",
                justifyContent: "center",
              }}
              className="nav-mobile-btn"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            style={{ background: "var(--navy-2)", borderLeft: "1px solid var(--border-gold)", width: 300 }}
          >
            <nav style={{ display: "flex", flexDirection: "column", gap: "0.5rem", paddingTop: "1rem" }}>
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: "1.2rem",
                  fontWeight: 400,
                  color: "var(--dp-white)",
                  paddingBottom: "1rem",
                  borderBottom: "1px solid var(--border-gold)",
                  marginBottom: "0.5rem",
                }}
              >
                Roberto<span style={{ color: "var(--gold)" }}> Pesce</span>
              </div>

              {isHomePage ? (
                <>
                  <button style={{ ...navLinkStyle, textAlign: "left", padding: "0.75rem 0" }} onClick={() => scrollTo("stories")}>Work</button>
                  <button style={{ ...navLinkStyle, textAlign: "left", padding: "0.75rem 0" }} onClick={() => scrollTo("about")}>About</button>
                  <button style={{ ...navLinkStyle, textAlign: "left", padding: "0.75rem 0" }} onClick={() => scrollTo("building")}>Building</button>
                  <button style={{ ...navLinkStyle, textAlign: "left", padding: "0.75rem 0" }} onClick={() => scrollTo("contact")}>Contact</button>
                  <a href="https://ehazzhswtowuskdmgibz.supabase.co/storage/v1/object/public/pescelab-main-site/CV%20Roberto%20Pesce.pdf" target="_blank" rel="noopener noreferrer" style={{ ...navLinkStyle, padding: "0.75rem 0", display: "inline-flex", alignItems: "center", gap: "0.4rem", color: "var(--gold)" }}>
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 13, height: 13 }}><path d="M8 2v8M5 7l3 3 3-3M3 12h10" /></svg>
                    Download CV
                  </a>
                </>
              ) : (
                <>
                  <Link to="/" style={{ ...navLinkStyle, padding: "0.75rem 0", display: "block" }} onClick={() => setMobileMenuOpen(false)}>Home</Link>
                  <a href="https://ehazzhswtowuskdmgibz.supabase.co/storage/v1/object/public/pescelab-main-site/CV%20Roberto%20Pesce.pdf" target="_blank" rel="noopener noreferrer" style={{ ...navLinkStyle, padding: "0.75rem 0", display: "inline-flex", alignItems: "center", gap: "0.4rem", color: "var(--gold)" }}>
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 13, height: 13 }}><path d="M8 2v8M5 7l3 3 3-3M3 12h10" /></svg>
                    Download CV
                  </a>
                </>
              )}

              <div
                style={{
                  paddingTop: "1rem",
                  borderTop: "1px solid var(--border-gold)",
                  marginTop: "0.5rem",
                  fontSize: "0.7rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "var(--dp-muted)",
                  marginBottom: "0.5rem",
                }}
              >
                Resources
              </div>
              {resourceItems.map((item) => (
                <Link
                  key={item.title}
                  to={item.href}
                  style={{ ...navLinkStyle, padding: "0.65rem 0", display: "block" }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.title}
                </Link>
              ))}
            </nav>
          </SheetContent>
        </Sheet>
      </nav>

      {/* ── Main ─────────────────────────────────────────── */}
      <main style={{ position: "relative", zIndex: 1, paddingTop: isHomePage ? 0 : "5rem" }}>{children}</main>

      {/* ── Footer ───────────────────────────────────────── */}
      <footer
        style={{
          background: "var(--navy)",
          borderTop: "1px solid var(--border-gold)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            padding: "2rem 5vw",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: "1.1rem",
              color: "var(--dp-white)",
            }}
          >
            Roberto<span style={{ color: "var(--gold)" }}> Pesce</span>
          </div>

          <div
            style={{
              fontSize: "0.78rem",
              color: "var(--dp-muted)",
              textAlign: "center",
              lineHeight: 1.8,
            }}
          >
© {new Date().getFullYear()} Roberto Pesce · All Rights Reserved · Amsterdam
          </div>

          <div style={{ display: "flex", gap: "1.5rem" }}>
            <a
              href="https://linkedin.com/in/robertopescejr"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "var(--dp-muted)",
                textDecoration: "none",
                fontSize: "0.8rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                transition: "color 0.2s",
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
              }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = "var(--gold)")}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "var(--dp-muted)")}
              aria-label="LinkedIn"
            >
              <Linkedin size={16} />
              LinkedIn
            </a>
            <a
              href="https://github.com/rpesce"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "var(--dp-muted)",
                textDecoration: "none",
                fontSize: "0.8rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                transition: "color 0.2s",
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
              }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = "var(--gold)")}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "var(--dp-muted)")}
              aria-label="GitHub"
            >
              <Github size={16} />
              GitHub
            </a>
          </div>
        </div>
      </footer>

      {/* Responsive nav toggle */}
      <style>{`
        @media (max-width: 900px) {
          .nav-desktop { display: none !important; }
          .nav-mobile-btn { display: flex !important; }
        }
      `}</style>
    </div>
  );
};

export default Layout;
